/**
 * GroupHomeDashboard — vue d'accueil par groupe (cf. polish post-ADR-027 P7+P8).
 *
 * Rendu dans la zone main de AppShell quand `pane === 'group_home'`. Mirror
 * du `HomeDashboard` mais scopé au groupe actif au lieu d'agréger
 * cross-groupes.
 *
 * Layout (P8 densification) :
 *   ┌──── Header : nom du groupe + memberCount + GroupMenu ────┐
 *   ├──── Grid 2-col de 4 Hero cards features ─────────────────┤
 *   │  EventsHero       │ PollsHero                              │
 *   │  ExpensesHero     │ TodosHero                              │
 *
 * Chaque Hero affiche un KPI principal + un teaser du prochain item d'action,
 * cliquable pour basculer sur le dashboard complet du feature. Pas de
 * doublon avec les dashboards eux-mêmes : ici c'est un coup d'œil dense, là
 * c'est l'expérience complète.
 *
 * Note design : les Hero des dashboards (NextEventHero, LivePollHero, etc.)
 * sont volontairement dupliqués en version compacte ici. Empiler 4 Hero
 * « pleine taille » serait écrasant. La version compacte garde la même
 * signature visuelle (gradient feature, border accent, icône, CTA flottant).
 */
import { useMemo } from 'react';

import { Avatar, PhIcon, type PhIconName } from '@/components/ui';
import { useAuth } from '@/lib/auth';
import {
  useEvents,
  useExpenses,
  useGroupMembers,
  usePolls,
  useTodoLists,
  type EventDto,
  type ExpenseDto,
  type Group,
  type GroupMember,
  type PollDto,
  type TodoListDto,
} from '@/lib/queries';
import { NX } from '@/lib/tokens';

import { ActivityTimeline } from './ActivityTimeline';
import { GroupMenu } from './GroupMenu';
import { topBandOffset, useAtWindowTop } from './TitleBar';
import { WeekCalendar } from './WeekCalendar';

// M1+M6 (post-ADR-027) : les sessions messageries ne sont plus scopées au
// groupe (elles sont user-scoped). Donc plus de section "Conversations
// connectées" ici. La nav reste vers les 4 panes feature du groupe.
export interface GroupHomeNavTarget {
  pane: 'event' | 'poll' | 'expense' | 'todo';
  sourceId?: string;
  /**
   * Jour visé (`YYYY-MM-DD`, local) — clic sur une case du calendrier semaine.
   * Le pane Événements s'ouvre positionné sur ce jour et liste tout ce qu'il
   * porte, au lieu d'ouvrir arbitrairement l'un de ses événements.
   */
  date?: string;
  /**
   * MAN-246 : le CTA d'une HeroCard vide annonce une création — il doit donc
   * en déclencher une, pas seulement changer de pane. Le shell traduit cette
   * intention en `pendingOpen`, que le dashboard cible consomme au montage.
   * Jamais posé sur la branche de consultation ni sur un KPI en erreur
   * (garde-fou MAN-244 : un compte inconnu n'est pas un compte nul).
   */
  create?: true;
}

interface GroupHomeDashboardProps {
  group: Group;
  onNavigate: (target: GroupHomeNavTarget) => void;
}

export function GroupHomeDashboard({ group, onNavigate }: GroupHomeDashboardProps) {
  const atWindowTop = useAtWindowTop();
  const user = useAuth((s) => s.user);
  const userId = user?.id ?? null;

  // `all` plutôt qu'`upcoming` : le calendrier semaine dessine Lundi → Dimanche
  // et doit donc pouvoir remplir les jours déjà écoulés — sinon il ne peut par
  // construction jamais afficher la première moitié de la semaine.
  //
  // Les deux autres consommateurs de cette liste (`EventsHero`,
  // `GroupUpcomingEvents`) refiltrent déjà `>= Date.now()` chez eux : leur
  // sémantique « à venir » survit donc à cet élargissement. Ne pas retirer ces
  // filtres internes en croyant que le parent s'en charge — c'est l'inverse.
  const eventsQ = useEvents(group.id, { when: 'all' });
  const allEvents = useMemo(() => eventsQ.data ?? [], [eventsQ.data]);
  const pollsQ = usePolls(group.id, { state: 'open' });
  const expensesQ = useExpenses(group.id, { state: 'open' });
  const todosQ = useTodoLists(group.id);
  const membersQ = useGroupMembers(group.id);

  const memberCount = membersQ.data?.length ?? 0;

  return (
    <div
      style={{
        flex: 1,
        minHeight: 0,
        display: 'flex',
        flexDirection: 'column',
        color: NX.fg,
      }}
    >
      {/* === Header groupe === */}
      <header
        // Au ras du haut de window (zone main d'`AppShell`) : le header porte la
        // drag region — Tauri exclut seul les contrôles de son sous-arbre — et
        // dégage la bande du cluster fenêtre, sous lequel tombe son action de
        // droite. Sous `MobileShell` il est rendu sous le header du stack
        // detail, donc ni l'un ni l'autre.
        {...(atWindowTop ? { 'data-tauri-drag-region': 'deep' } : {})}
        style={{
          padding: `${atWindowTop ? topBandOffset(NX.spaceDashboard) : NX.spaceDashboard}px ${NX.spaceDashboardLg}px ${NX.spaceDashboard - 4}px`,
          display: 'flex',
          alignItems: 'center',
          gap: 14,
          borderBottom: `0.5px solid ${NX.border}`,
        }}
      >
        <Avatar name={group.name} size={40} />
        <div style={{ minWidth: 0, flex: 1 }}>
          <div style={{ fontSize: 18, fontWeight: 600, letterSpacing: '-0.01em' }}>
            {group.name}
          </div>
          <div style={{ fontSize: 12, color: NX.fgMuted, marginTop: 2 }}>
            {memberCount} membre{memberCount > 1 ? 's' : ''}
          </div>
        </div>
        <GroupMenu group={group} />
      </header>

      {/* === Body : 4 Hero cards + WeekCalendar + ActivityTimeline === */}
      <main
        style={{
          flex: 1,
          minHeight: 0,
          overflow: 'auto',
          padding: `${NX.spaceDashboard}px ${NX.spaceDashboardLg}px ${NX.spaceDashboardLg}px`,
          display: 'flex',
          flexDirection: 'column',
          gap: 18,
        }}
      >
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(380px, 1fr))',
            gap: 16,
          }}
        >
          {/* MAN-244 : `isPending` et pas `isLoading` — ces queries sont
              désactivées le temps que l'auth se résolve, et en TanStack v5 une
              query désactivée rapporte `isLoading === false` avec
              `isPending === true`. Et `isError` remonté pour que les KPI
              n'affichent pas un compte inventé (cf. les Hero ci-dessous). */}
          <EventsHero
            events={allEvents}
            isPending={eventsQ.isPending}
            isError={eventsQ.isError}
            onOpen={(sourceId) => onNavigate({ pane: 'event', ...(sourceId ? { sourceId } : {}) })}
            onCreate={() => onNavigate({ pane: 'event', create: true })}
          />
          <PollsHero
            polls={pollsQ.data ?? []}
            userId={userId}
            isPending={pollsQ.isPending}
            isError={pollsQ.isError}
            onOpen={(sourceId) => onNavigate({ pane: 'poll', ...(sourceId ? { sourceId } : {}) })}
            onCreate={() => onNavigate({ pane: 'poll', create: true })}
          />
          <ExpensesHero
            expenses={expensesQ.data ?? []}
            userId={userId}
            isPending={expensesQ.isPending}
            isError={expensesQ.isError}
            onOpen={(sourceId) =>
              onNavigate({ pane: 'expense', ...(sourceId ? { sourceId } : {}) })
            }
            onCreate={() => onNavigate({ pane: 'expense', create: true })}
          />
          <TodosHero
            lists={todosQ.data ?? []}
            userId={userId}
            isPending={todosQ.isPending}
            isError={todosQ.isError}
            onOpen={(sourceId) => onNavigate({ pane: 'todo', ...(sourceId ? { sourceId } : {}) })}
            onCreate={() => onNavigate({ pane: 'todo', create: true })}
          />
        </div>

        {/* Mirror du HomeDashboard (post-2026-05-05) : calendrier semaine
            scopé aux events du groupe + timeline d'activité scopée. Cohérence
            visuelle avec la home cross-groupes. */}
        <WeekCalendar
          events={allEvents}
          onDayClick={(day) => onNavigate({ pane: 'event', date: isoDayLocal(day.date) })}
        />

        {/* Balance dépenses du groupe : qui doit à qui, en un coup d'œil.
            Mirror du HomeDashboard ExpenseBalance, scopé au groupe. */}
        <GroupExpenseBalance
          expenses={expensesQ.data ?? []}
          members={membersQ.data ?? []}
          userId={userId}
        />

        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))',
            gap: 14,
          }}
        >
          <GroupPendingPolls polls={pollsQ.data ?? []} userId={userId} onNavigate={onNavigate} />
          <GroupUpcomingEvents events={allEvents} onNavigate={onNavigate} />
        </div>

        <GroupActivitySection groupId={group.id} onNavigate={onNavigate} />
      </main>
    </div>
  );
}

/**
 * Section "Activité récente" scopée au groupe courant. Wrappe ActivityTimeline
 * dans une section style-Card sans chip groupe (le contexte est déjà clair).
 */
function GroupActivitySection({
  groupId,
  onNavigate,
}: {
  groupId: string;
  onNavigate: (target: GroupHomeNavTarget) => void;
}) {
  return (
    <section
      style={{
        background: NX.surface,
        border: `1px solid ${NX.border}`,
        borderRadius: NX.radiusLg,
        padding: '14px 14px 12px',
      }}
    >
      <header style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
        <div
          style={{
            width: 28,
            height: 28,
            borderRadius: 8,
            background: NX.accentBg,
            color: NX.accent,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
          }}
        >
          <PhIcon name="clock" size={15} />
        </div>
        <div style={{ flex: 1, fontSize: 13, fontWeight: 600, color: NX.fg }}>Activité récente</div>
      </header>
      <ActivityTimeline
        groupId={groupId}
        onNavigate={(t) => {
          // ActivityNavTarget → GroupHomeNavTarget. La pane 'chat' n'a pas
          // d'équivalent dans GroupHomeNavTarget (qui ne gère que les 4
          // features), donc on filtre.
          if (t.pane === 'chat') return;
          onNavigate({
            pane: t.pane,
            ...(t.sourceId !== undefined ? { sourceId: t.sourceId } : {}),
          });
        }}
      />
    </section>
  );
}

// ─────────────────────── Hero compacts par feature ──────────────────────

function HeroCard({
  icon,
  iconColor,
  iconBg,
  accent,
  accentSoftBg,
  label,
  kpiValue,
  kpiUnit,
  teaserTitle,
  teaserMeta,
  ctaLabel,
  onOpen,
  onCta,
  onTeaserClick,
}: {
  icon: PhIconName;
  iconColor: string;
  iconBg: string;
  accent: string;
  accentSoftBg: string;
  label: string;
  kpiValue: string;
  kpiUnit?: string | undefined;
  teaserTitle?: string | undefined;
  teaserMeta?: string | undefined;
  ctaLabel: string;
  onOpen: () => void;
  /**
   * Action du bouton CTA quand elle diffère de `onOpen` — c'est-à-dire quand
   * le libellé annonce une création (MAN-246). Par défaut le CTA ouvre le
   * dashboard, comme avant.
   */
  onCta?: (() => void) | undefined;
  onTeaserClick?: (() => void) | undefined;
}) {
  return (
    <section
      style={{
        background: `linear-gradient(135deg, ${accentSoftBg} 0%, transparent 70%), ${NX.surface}`,
        border: `0.5px solid ${accent}33`,
        borderRadius: NX.radiusXl,
        padding: 18,
        display: 'flex',
        flexDirection: 'column',
        gap: 14,
        minHeight: 180,
      }}
    >
      {/* Header : icône + label */}
      <header style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        <div
          style={{
            width: 32,
            height: 32,
            borderRadius: 9,
            background: iconBg,
            color: iconColor,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
          }}
        >
          <PhIcon name={icon} size={17} />
        </div>
        <div
          style={{
            fontSize: 11,
            textTransform: 'uppercase',
            letterSpacing: '0.12em',
            color: accent,
            fontWeight: 600,
          }}
        >
          {label}
        </div>
      </header>

      {/* KPI principal */}
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 6 }}>
        <span
          style={{
            fontSize: 32,
            fontWeight: 700,
            letterSpacing: '-0.02em',
            color: NX.fg,
            lineHeight: 1,
          }}
        >
          {kpiValue}
        </span>
        {kpiUnit ? (
          <span style={{ fontSize: 13, color: NX.fgMuted, fontWeight: 500 }}>{kpiUnit}</span>
        ) : null}
      </div>

      {/* Teaser (optionnel) — clique vers l'item précis */}
      {teaserTitle ? (
        <button
          type="button"
          onClick={onTeaserClick ?? onOpen}
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'flex-start',
            gap: 2,
            padding: '8px 10px',
            background: NX.elevated,
            border: `0.5px solid ${NX.border}`,
            borderRadius: NX.radiusSm,
            cursor: 'pointer',
            color: 'inherit',
            textAlign: 'left',
            transition: 'border-color 120ms',
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.borderColor = accent;
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.borderColor = NX.border;
          }}
        >
          <span
            style={{
              fontSize: 13,
              fontWeight: 500,
              color: NX.fg,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              maxWidth: '100%',
            }}
          >
            {teaserTitle}
          </span>
          {teaserMeta ? <span style={{ fontSize: 11, color: NX.fgDim }}>{teaserMeta}</span> : null}
        </button>
      ) : null}

      {/* CTA */}
      <div style={{ flex: 1 }} />
      <button
        type="button"
        onClick={onCta ?? onOpen}
        style={{
          alignSelf: 'flex-start',
          display: 'inline-flex',
          alignItems: 'center',
          gap: 6,
          padding: '7px 14px',
          background: accent,
          color: '#fff',
          border: 'none',
          borderRadius: NX.radiusPill,
          fontSize: 12,
          fontWeight: 600,
          cursor: 'pointer',
        }}
      >
        {ctaLabel}
        <PhIcon name="arrowRight" size={13} color="#fff" />
      </button>
    </section>
  );
}

function EventsHero({
  events,
  isPending,
  isError,
  onOpen,
  onCreate,
}: {
  events: EventDto[];
  isPending: boolean;
  isError: boolean;
  onOpen: (sourceId?: string) => void;
  /**
   * Émet l'intention de création (MAN-246). Appelé uniquement depuis la
   * branche « vide » du CTA — celle dont le libellé promet une création.
   */
  onCreate: () => void;
}) {
  const upcoming = useMemo(
    () =>
      events
        .filter((e) => new Date(e.startsAt).getTime() >= Date.now())
        .sort((a, b) => new Date(a.startsAt).getTime() - new Date(b.startsAt).getTime()),
    [events],
  );
  const next = upcoming[0];
  const count = upcoming.length;

  // MAN-244 : sur échec, `—` et non `0`. Un compte inconnu ne doit pas être
  // présenté comme un compte nul — et surtout le CTA ne doit pas basculer sur
  // « Créer un événement », ce qui pousserait l'utilisateur à recréer ce qui
  // existe peut-être déjà.
  const kpiValue = isError ? '—' : isPending ? '…' : String(count);
  // `ctaCreates` porte la condition une seule fois : le libellé et l'action
  // qu'il déclenche ne peuvent plus diverger — c'est très exactement la
  // divergence que MAN-246 corrige (le libellé promettait, l'action naviguait).
  const ctaCreates = !isError && count === 0;
  const ctaLabel = ctaCreates ? 'Créer un événement' : "Voir l'agenda";

  return (
    <HeroCard
      icon="calendarBlank"
      iconColor={NX.featEvents}
      iconBg={NX.featEventsBg}
      accent={NX.featEvents}
      accentSoftBg={NX.featEventsBg}
      label="Événements"
      kpiValue={kpiValue}
      kpiUnit={isError ? 'indisponible' : 'à venir'}
      teaserTitle={next?.title}
      teaserMeta={
        next
          ? formatRelativeDate(next.startsAt) + (next.location ? ` · ${next.location}` : '')
          : undefined
      }
      ctaLabel={ctaLabel}
      onOpen={() => onOpen()}
      onCta={ctaCreates ? onCreate : undefined}
      onTeaserClick={next ? () => onOpen(next.id) : undefined}
    />
  );
}

function PollsHero({
  polls,
  userId,
  isPending,
  isError,
  onOpen,
  onCreate,
}: {
  polls: PollDto[];
  userId: string | null;
  isPending: boolean;
  isError: boolean;
  onOpen: (sourceId?: string) => void;
  /**
   * Émet l'intention de création (MAN-246). Appelé uniquement depuis la
   * branche « vide » du CTA — celle dont le libellé promet une création.
   */
  onCreate: () => void;
}) {
  const pendingForMe = useMemo(() => {
    if (!userId) return polls;
    return polls.filter((p) => !p.options.some((o) => o.voters.includes(userId)));
  }, [polls, userId]);
  const next = pendingForMe[0];

  // MAN-244 : cf. `EventsHero` — un compte inconnu n'est pas un compte nul, et
  // le CTA ne bascule pas sur la création quand on ne sait pas.
  const kpiValue = isError ? '—' : isPending ? '…' : String(pendingForMe.length);
  const ctaCreates = !isError && pendingForMe.length === 0;
  const ctaLabel = ctaCreates ? 'Lancer un sondage' : 'Voir les sondages';

  return (
    <HeroCard
      icon="chartBar"
      iconColor={NX.featPolls}
      iconBg={NX.featPollsBg}
      accent={NX.featPolls}
      accentSoftBg={NX.featPollsBg}
      label="Sondages"
      kpiValue={kpiValue}
      kpiUnit={isError ? 'indisponible' : 'en attente de toi'}
      teaserTitle={next?.question}
      teaserMeta={
        next
          ? `${next.options.length} options${next.closesAt ? ` · ferme ${formatRelativeDate(next.closesAt)}` : ''}`
          : undefined
      }
      ctaLabel={ctaLabel}
      onOpen={() => onOpen()}
      onCta={ctaCreates ? onCreate : undefined}
      onTeaserClick={next ? () => onOpen(next.id) : undefined}
    />
  );
}

function ExpensesHero({
  expenses,
  userId,
  isPending,
  isError,
  onOpen,
  onCreate,
}: {
  expenses: ExpenseDto[];
  userId: string | null;
  isPending: boolean;
  isError: boolean;
  onOpen: (sourceId?: string) => void;
  /**
   * Émet l'intention de création (MAN-246). Appelé uniquement depuis la
   * branche « vide » du CTA — celle dont le libellé promet une création.
   */
  onCreate: () => void;
}) {
  // Solde net : ∑(montants payés par moi non réglés par les autres)
  //          − ∑(parts non réglées que je dois)
  // Si > 0 : on me doit. Si < 0 : je dois. Si 0 : équilibre.
  const { netCents, currency, myOpenCount, nextOpen } = useMemo(() => {
    let net = 0;
    let curr = 'EUR';
    let count = 0;
    let firstOpen: ExpenseDto | undefined;
    for (const e of expenses) {
      if (e.settledAt) continue;
      curr = e.currency;
      // Mes parts non réglées que je dois
      if (userId) {
        const myShare = e.shares.find((s) => s.userId === userId && !s.isSettled);
        if (myShare) {
          count += 1;
          firstOpen ??= e;
          // Si c'est moi qui ai payé, mes parts à moi-même sont gratuites
          if (e.paidBy !== userId) net -= myShare.shareCents;
        }
        // Parts dues par les autres si c'est moi qui ai payé
        if (e.paidBy === userId) {
          for (const s of e.shares) {
            if (s.userId === userId) continue;
            if (!s.isSettled) net += s.shareCents;
          }
        }
      }
    }
    return { netCents: net, currency: curr, myOpenCount: count, nextOpen: firstOpen };
  }, [expenses, userId]);

  const isPositive = netCents > 0;
  const absLabel = formatMoney(Math.abs(netCents), currency);
  // MAN-244 : c'est le KPI le plus dommageable des quatre. Sur échec, l'ancienne
  // version affichait `formatMoney(0)` avec l'unité « tout est réglé » — elle
  // affirmait à l'utilisateur qu'il ne devait rien, sur la base d'aucune
  // donnée. Un solde inconnu s'affiche `—`, jamais zéro.
  const kpiValue = isError
    ? '—'
    : isPending
      ? '…'
      : netCents === 0
        ? formatMoney(0, currency)
        : (isPositive ? '+' : '−') + absLabel;
  const kpiUnit = isError
    ? 'indisponible'
    : netCents === 0
      ? 'tout est réglé'
      : isPositive
        ? 'on te doit'
        : 'tu dois';
  // Même invariant que les 3 autres héros : une seule condition pour le
  // libellé et pour l'action qu'il déclenche.
  const ctaCreates = !isError && myOpenCount === 0 && netCents === 0;

  return (
    <HeroCard
      icon="currencyDollar"
      iconColor={NX.featExpenses}
      iconBg={NX.featExpensesBg}
      accent={NX.featExpenses}
      accentSoftBg={NX.featExpensesBg}
      label="Dépenses"
      kpiValue={kpiValue}
      kpiUnit={kpiUnit}
      teaserTitle={
        myOpenCount > 0
          ? nextOpen?.description
          : isPositive
            ? 'Réclame ton dû ou marque comme réglé'
            : undefined
      }
      teaserMeta={
        myOpenCount > 0
          ? `${myOpenCount} dépense${myOpenCount > 1 ? 's' : ''} ouverte${myOpenCount > 1 ? 's' : ''}`
          : undefined
      }
      ctaLabel={ctaCreates ? 'Ajouter une dépense' : 'Voir les soldes'}
      onOpen={() => onOpen()}
      onCta={ctaCreates ? onCreate : undefined}
      onTeaserClick={nextOpen ? () => onOpen(nextOpen.id) : undefined}
    />
  );
}

function TodosHero({
  lists,
  userId,
  isPending,
  isError,
  onOpen,
  onCreate,
}: {
  lists: TodoListDto[];
  userId: string | null;
  isPending: boolean;
  isError: boolean;
  onOpen: (sourceId?: string) => void;
  /**
   * Émet l'intention de création (MAN-246). Appelé uniquement depuis la
   * branche « vide » du CTA — celle dont le libellé promet une création.
   */
  onCreate: () => void;
}) {
  const { myOpenCount, nextItem, nextListTitle } = useMemo(() => {
    if (!userId)
      return {
        myOpenCount: 0,
        nextItem: undefined as TodoListDto['items'][number] | undefined,
        nextListTitle: undefined as string | undefined,
      };
    let count = 0;
    let firstItem: TodoListDto['items'][number] | undefined;
    let firstListTitle: string | undefined;
    for (const list of lists) {
      for (const item of list.items) {
        if (item.done) continue;
        if (item.assigneeId !== userId) continue;
        count += 1;
        if (!firstItem) {
          firstItem = item;
          firstListTitle = list.title;
        }
      }
    }
    return { myOpenCount: count, nextItem: firstItem, nextListTitle: firstListTitle };
  }, [lists, userId]);
  const ctaCreates = !isError && lists.length === 0;

  return (
    <HeroCard
      icon="listChecks"
      iconColor={NX.featTodo}
      iconBg={NX.featTodoBg}
      accent={NX.featTodo}
      accentSoftBg={NX.featTodoBg}
      label="Mes tâches"
      kpiValue={isError ? '—' : isPending ? '…' : String(myOpenCount)}
      kpiUnit={isError ? 'indisponible' : myOpenCount === 0 ? 'tout est fait' : 'à faire'}
      teaserTitle={nextItem?.text}
      teaserMeta={nextListTitle}
      ctaLabel={ctaCreates ? 'Créer une liste' : 'Voir mes tâches'}
      onOpen={() => onOpen()}
      onCta={ctaCreates ? onCreate : undefined}
      onTeaserClick={nextItem ? () => onOpen(nextItem.id) : undefined}
    />
  );
}

// ─────────────────────── Card / Row primitives ──────────────────────────

function Card({
  icon,
  color,
  colorBg,
  title,
  count,
  empty,
  children,
}: {
  icon: PhIconName;
  color: string;
  colorBg: string;
  title: string;
  count: number;
  empty: string;
  children: React.ReactNode;
}) {
  const hasItems = count > 0;
  return (
    <section
      style={{
        background: NX.surface,
        border: `1px solid ${NX.border}`,
        borderRadius: NX.radiusLg,
        padding: '14px 14px 10px',
        display: 'flex',
        flexDirection: 'column',
        gap: 8,
      }}
    >
      <header style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        <div
          style={{
            width: 28,
            height: 28,
            borderRadius: 8,
            background: colorBg,
            color,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
          }}
        >
          <PhIcon name={icon} size={15} />
        </div>
        <div style={{ flex: 1, fontSize: 13, fontWeight: 600, color: NX.fg }}>{title}</div>
        {hasItems ? (
          <span
            style={{
              fontSize: 11,
              fontWeight: 700,
              padding: '2px 8px',
              borderRadius: NX.radiusPill,
              background: colorBg,
              color,
            }}
          >
            {count}
          </span>
        ) : null}
      </header>
      {hasItems ? (
        <div style={{ display: 'flex', flexDirection: 'column' }}>{children}</div>
      ) : (
        <div style={{ padding: '12px 4px', fontSize: 12, color: NX.fgDim }}>{empty}</div>
      )}
    </section>
  );
}

function RowButton({ onClick, children }: { onClick: () => void; children: React.ReactNode }) {
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 10,
        padding: '8px 6px',
        background: 'transparent',
        border: 'none',
        cursor: 'pointer',
        borderRadius: NX.radiusSm,
        textAlign: 'left',
        color: 'inherit',
        transition: 'background 120ms',
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.background = NX.elevated;
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.background = 'transparent';
      }}
    >
      {children}
    </button>
  );
}

// ─────────────────────── Balance dépenses (scopée groupe) ────────────────

/**
 * "Qui doit à qui" au sein du groupe, calculé depuis les dépenses ouvertes.
 * Pour chaque part non réglée d'une dépense, le porteur de la part doit le
 * montant au payeur (sauf si payeur == porteur). On agrège par couple
 * (debtor → creditor), puis on nette par couple pour ne pas afficher des
 * dettes croisées (A doit 10 à B et B doit 4 à A → A doit 6 à B).
 *
 * Les paires impliquant l'utilisateur courant sont remontées en tête et
 * formulées à la 1re personne ("Tu dois …" / "… te doit").
 */
function GroupExpenseBalance({
  expenses,
  members,
  userId,
}: {
  expenses: ExpenseDto[];
  members: GroupMember[];
  userId: string | null;
}) {
  const nameOf = useMemo(() => {
    const m = new Map<string, string>();
    for (const mem of members) m.set(mem.userId, mem.displayName);
    return (id: string) => m.get(id) ?? 'Quelqu’un';
  }, [members]);

  const { pairs, currency } = useMemo(() => {
    // key = `${debtor}>${creditor}` → cents
    const raw = new Map<string, number>();
    let curr = 'EUR';
    for (const e of expenses) {
      if (e.settledAt) continue;
      curr = e.currency;
      for (const s of e.shares) {
        if (s.isSettled) continue;
        if (s.userId === e.paidBy) continue;
        const key = `${s.userId}>${e.paidBy}`;
        raw.set(key, (raw.get(key) ?? 0) + s.shareCents);
      }
    }
    // Nette les paires croisées : pour chaque couple non ordonné {a,b},
    // on garde le solde net dans une seule direction.
    const seen = new Set<string>();
    const out: { debtor: string; creditor: string; cents: number }[] = [];
    for (const [key, cents] of raw) {
      const [debtor, creditor] = key.split('>') as [string, string];
      const reverseKey = `${creditor}>${debtor}`;
      const pairId = [debtor, creditor].sort().join('|');
      if (seen.has(pairId)) continue;
      seen.add(pairId);
      const reverse = raw.get(reverseKey) ?? 0;
      const net = cents - reverse;
      if (net > 0) out.push({ debtor, creditor, cents: net });
      else if (net < 0) out.push({ debtor: creditor, creditor: debtor, cents: -net });
      // net === 0 → équilibre parfait, rien à afficher
    }
    // Tri : paires impliquant l'user d'abord, puis montant décroissant.
    out.sort((a, b) => {
      const aMe = a.debtor === userId || a.creditor === userId ? 0 : 1;
      const bMe = b.debtor === userId || b.creditor === userId ? 0 : 1;
      if (aMe !== bMe) return aMe - bMe;
      return b.cents - a.cents;
    });
    return { pairs: out, currency: curr };
  }, [expenses, userId]);

  return (
    <section
      style={{
        background: NX.surface,
        border: `1px solid ${NX.border}`,
        borderRadius: NX.radiusLg,
        padding: '14px 14px 12px',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
        <div
          style={{
            width: 28,
            height: 28,
            borderRadius: 8,
            background: NX.featExpensesBg,
            color: NX.featExpenses,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <PhIcon name="currencyDollar" size={15} />
        </div>
        <div style={{ flex: 1, fontSize: 13, fontWeight: 600, color: NX.fg }}>Qui doit à qui</div>
      </div>
      {pairs.length === 0 ? (
        <div style={{ padding: '8px 4px', fontSize: 12, color: NX.fgDim }}>
          Tous les comptes sont équilibrés.
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          {pairs.map((p) => {
            const debtorIsMe = p.debtor === userId;
            const creditorIsMe = p.creditor === userId;
            const debtorLabel = debtorIsMe ? 'Tu' : nameOf(p.debtor);
            const verb = debtorIsMe ? 'dois' : 'doit';
            const creditorLabel = creditorIsMe ? 'toi' : nameOf(p.creditor);
            const accent = debtorIsMe || creditorIsMe;
            const avatarName = debtorIsMe ? nameOf(p.creditor) : nameOf(p.debtor);
            return (
              <div
                key={`${p.debtor}>${p.creditor}`}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 10,
                  padding: '6px 4px',
                  fontSize: 13,
                }}
              >
                <Avatar name={avatarName} size={24} />
                <div style={{ flex: 1, color: NX.fg }}>
                  <span style={{ fontWeight: 600 }}>{debtorLabel}</span> {verb}{' '}
                  <span style={{ fontWeight: 600 }}>{creditorLabel}</span>
                </div>
                <div
                  style={{
                    fontWeight: 600,
                    color: accent ? NX.featExpenses : NX.fgMuted,
                    fontVariantNumeric: 'tabular-nums',
                  }}
                >
                  {formatMoney(p.cents, currency)}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </section>
  );
}

// ─────────────────────── Sondages en attente (scopés groupe) ─────────────

function GroupPendingPolls({
  polls,
  userId,
  onNavigate,
}: {
  polls: PollDto[];
  userId: string | null;
  onNavigate: (target: GroupHomeNavTarget) => void;
}) {
  const pendingForMe = useMemo(() => {
    if (!userId) return polls;
    return polls.filter((p) => !p.options.some((o) => o.voters.includes(userId)));
  }, [polls, userId]);

  return (
    <Card
      icon="chartBar"
      color={NX.featPolls}
      colorBg={NX.featPollsBg}
      title="Sondages en attente"
      count={pendingForMe.length}
      empty="Tu as voté à tous les sondages ouverts."
    >
      {pendingForMe.map((p) => {
        const closesLabel = p.closesAt
          ? `Ferme ${formatRelativeDate(p.closesAt)}`
          : `${p.options.length} option${p.options.length > 1 ? 's' : ''}`;
        return (
          <RowButton key={p.id} onClick={() => onNavigate({ pane: 'poll', sourceId: p.id })}>
            <PhIcon name="chartBar" size={14} color={NX.featPolls} />
            <div style={{ flex: 1, minWidth: 0 }}>
              <div
                style={{
                  fontSize: 13,
                  color: NX.fg,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {p.question}
              </div>
              <div style={{ fontSize: 11, color: NX.fgDim, marginTop: 1 }}>{closesLabel}</div>
            </div>
          </RowButton>
        );
      })}
    </Card>
  );
}

// ─────────────────────── Prochains events (scopés groupe) ────────────────

function GroupUpcomingEvents({
  events,
  onNavigate,
}: {
  events: EventDto[];
  onNavigate: (target: GroupHomeNavTarget) => void;
}) {
  const upcoming = useMemo(
    () =>
      events
        .filter((e) => new Date(e.startsAt).getTime() >= Date.now())
        .sort((a, b) => new Date(a.startsAt).getTime() - new Date(b.startsAt).getTime()),
    [events],
  );

  return (
    <Card
      icon="calendarBlank"
      color={NX.featEvents}
      colorBg={NX.featEventsBg}
      title="Prochains events"
      count={upcoming.length}
      empty="Aucun event à venir."
    >
      {upcoming.map((e) => (
        <RowButton key={e.id} onClick={() => onNavigate({ pane: 'event', sourceId: e.id })}>
          <PhIcon name="calendarBlank" size={14} color={NX.featEvents} />
          <div style={{ flex: 1, minWidth: 0 }}>
            <div
              style={{
                fontSize: 13,
                color: NX.fg,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {e.title}
            </div>
            <div style={{ fontSize: 11, color: NX.fgDim, marginTop: 1 }}>
              {formatRelativeDate(e.startsAt)}
              {e.location ? ` · ${e.location}` : ''}
            </div>
          </div>
        </RowButton>
      ))}
    </Card>
  );
}

// ─────────────────────── Format helpers ─────────────────────────

function formatRelativeDate(iso: string): string {
  const d = new Date(iso);
  const now = Date.now();
  const diffMin = Math.round((d.getTime() - now) / 60_000);
  const abs = Math.abs(diffMin);
  if (abs < 60) {
    return diffMin >= 0 ? `dans ${abs} min` : `il y a ${abs} min`;
  }
  const diffH = Math.round(diffMin / 60);
  if (Math.abs(diffH) < 24) {
    return diffH >= 0 ? `dans ${diffH} h` : `il y a ${Math.abs(diffH)} h`;
  }
  const diffD = Math.round(diffH / 24);
  if (Math.abs(diffD) < 7) {
    return diffD >= 0 ? `dans ${diffD} j` : `il y a ${Math.abs(diffD)} j`;
  }
  return d.toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' });
}

function formatMoney(cents: number, currency: string): string {
  try {
    return new Intl.NumberFormat('fr-FR', {
      style: 'currency',
      currency,
      minimumFractionDigits: 2,
    }).format(cents / 100);
  } catch {
    return `${(cents / 100).toFixed(2)} ${currency}`;
  }
}

/**
 * Jour local au format `YYYY-MM-DD`.
 *
 * Pas `toISOString().slice(0, 10)` : celui-ci convertit en UTC d'abord, donc un
 * événement du 15 à 23 h à Paris devient le 16 — le calendrier renverrait un
 * jour que l'utilisateur n'a pas cliqué.
 */
function isoDayLocal(d: Date): string {
  const pad = (n: number) => n.toString().padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}
